import { makeAutoObservable, runInAction } from 'mobx';
import {
    calculateFairPricePE,
    calculateFairPriceFCF,
    calculateFairPriceDCF,
    calculateCAGR,
    calculateProjectionPE,
    calculateProjectionFCF,
    calculateProjectionDCF,
    calculateDCFTerminalValueWeight,
    DCF_YEARS,
    FairPriceMethodEnum,
} from '@app/common';
import type { RootStore } from './root-store';
import { FairPriceFormValues } from '../../components/app/fair-price/form-values/fair-price-form-values-interface';
import { UpsertSymbolFairPriceDtoSelectedMethodEnum } from '../../defs/api';
import { AreaChartMobxDto } from '../mobx/dtos/statistic/area-chart-mobx-dto';
import { AreaChartDataMobxDto } from '../mobx/dtos/statistic/area-chart-data-mobx-dto';

import { stripSpaces, formatWithSpaces } from '../../utils/format-with-spaces';

export type FairPriceTab = FairPriceMethodEnum;

export class SymbolFairPriceStore {
    rootStore: RootStore;

    loading = true;

    symbol: string = '';

    inputEps: number | null = null;

    inputPeMultiple: number | null = null;

    inputGrowthRate: number | null = null;

    inputPeRequiredReturn: number | null = null;

    inputFcfRequiredReturn: number | null = null;

    inputGrowthRateFCF: number | null = null;

    inputFcfPerShare: number | null = null;

    inputTargetFCFYield: number | null = null;

    inputDcfTotalFcf: number | null = null;

    apiDcfTotalFcf: number | null = null;

    apiDcfCash: number | null = null;

    apiDcfDebt: number | null = null;

    apiDcfSharesOutstanding: number | null = null;

    inputDcfGrowthRate: number | null = null;

    inputTerminalGrowthRate: number | null = null;

    inputDiscountRate: number | null = null;

    dcfYears: 5 | 10 = DCF_YEARS;

    inputMarginOfSafety: number | null = null;

    inputDcfCash: number | null = null;

    inputDcfDebt: number | null = null;

    inputDcfSharesOutstanding: number | null = null;

    eps: number | null = null;

    peRatio: number | null = null;

    fcfPerShare: number | null = null;

    fcfYield: number | null = null;

    activeTab: FairPriceTab = FairPriceMethodEnum.PE;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, { rootStore: false });
    }

    get currentPrice(): number {
        return (
            this.rootStore.symbolsStore.symbolDetailStore?.symbolDetailChartStore?.absoluteCloses?.data.slice(-1)[0]
                ?.y ?? 0
        );
    }

    get activeFairPrice(): number {
        if (this.activeTab === FairPriceMethodEnum.PE) return this.fairPricePE;
        if (this.activeTab === FairPriceMethodEnum.FCF) return this.fairPriceFCF;
        if (this.activeTab === FairPriceMethodEnum.DCF) return this.intrinsicValueDCF;
        return 0;
    }

    get fairPrice(): number {
        return Math.round(this.activeFairPrice);
    }

    get fairPricePE(): number {
        const eps = this.inputEps;
        const peMultiple = this.inputPeMultiple;
        if (!eps || !peMultiple || !this.inputGrowthRate || !this.inputPeRequiredReturn) return 0;
        return calculateFairPricePE(eps, this.inputGrowthRate, peMultiple, this.inputPeRequiredReturn);
    }

    get fairPriceFCF(): number {
        const fcf = this.inputFcfPerShare;
        const growthRate = this.inputGrowthRateFCF ?? this.inputGrowthRate;
        if (!fcf || !this.inputFcfRequiredReturn || !this.inputTargetFCFYield || !growthRate) return 0;
        return calculateFairPriceFCF(fcf, null, growthRate, this.inputTargetFCFYield, this.inputFcfRequiredReturn);
    }

    get intrinsicValueDCF(): number {
        const fcf = this.inputDcfTotalFcf;
        const shares = this.inputDcfSharesOutstanding;
        if (
            !fcf ||
            !this.inputDcfGrowthRate ||
            this.inputTerminalGrowthRate == null ||
            !this.inputDiscountRate ||
            !shares ||
            shares <= 0
        ) {
            return 0;
        }
        return calculateFairPriceDCF(
            fcf,
            this.inputDcfGrowthRate,
            this.inputTerminalGrowthRate,
            this.inputDiscountRate,
            this.dcfYears,
            this.inputDcfCash ?? 0,
            this.inputDcfDebt ?? 0,
            shares,
        );
    }

    get fairPriceDCF(): number {
        const iv = this.intrinsicValueDCF;
        if (!iv) return 0;
        return iv * (1 - (this.inputMarginOfSafety ?? 0) / 100);
    }

    get terminalValueWeight(): number {
        const fcf = this.inputDcfTotalFcf;
        if (!fcf || !this.inputDcfGrowthRate || this.inputTerminalGrowthRate == null || !this.inputDiscountRate) {
            return 0;
        }
        return calculateDCFTerminalValueWeight(
            fcf,
            this.inputDcfGrowthRate,
            this.inputTerminalGrowthRate,
            this.inputDiscountRate,
            this.dcfYears,
        );
    }

    get upsideDownsideDCF(): number {
        // CAGR z dnešní ceny na konec projekce v grafu (s MOS).
        if (!this.inputDiscountRate) return 0;
        const yEnd = this.fairPriceDCF * (1 + this.inputDiscountRate / 100) ** this.dcfYears;
        return calculateCAGR(yEnd, this.currentPrice, this.dcfYears);
    }

    get upsideDownsidePE(): number {
        // CAGR z dnešní ceny na konec projekce v grafu.
        if (!this.fairPricePE || !this.inputPeRequiredReturn) return 0;
        const y5 = this.fairPricePE * (1 + this.inputPeRequiredReturn / 100) ** DCF_YEARS;
        return calculateCAGR(y5, this.currentPrice, DCF_YEARS);
    }

    get upsideDownsideFCF(): number {
        // CAGR z dnešní ceny na konec projekce v grafu.
        if (!this.fairPriceFCF || !this.inputFcfRequiredReturn) return 0;
        const y5 = this.fairPriceFCF * (1 + this.inputFcfRequiredReturn / 100) ** DCF_YEARS;
        return calculateCAGR(y5, this.currentPrice, DCF_YEARS);
    }

    get formValues(): FairPriceFormValues {
        return {
            peRequiredReturn: this.inputPeRequiredReturn != null ? String(this.inputPeRequiredReturn) : '',
            fcfRequiredReturn: this.inputFcfRequiredReturn != null ? String(this.inputFcfRequiredReturn) : '',
            growthRate: this.inputGrowthRate,
            eps: this.inputEps?.toFixed(2) ?? '',
            peMultiple: this.inputPeMultiple?.toFixed(2) ?? '',
            fcfPerShare: this.inputFcfPerShare?.toFixed(2) ?? '',
            fcfGrowthRate: this.inputGrowthRateFCF != null ? String(this.inputGrowthRateFCF) : '',
            fcfTargetYield: this.inputTargetFCFYield?.toFixed(2) ?? '',

            dcfTotalFcf: this.inputDcfTotalFcf != null ? formatWithSpaces(this.inputDcfTotalFcf / 1e6) : '',
            dcfGrowthRate: this.inputDcfGrowthRate != null ? String(this.inputDcfGrowthRate) : '',
            dcfTerminalGrowthRate: this.inputTerminalGrowthRate != null ? String(this.inputTerminalGrowthRate) : '',
            dcfDiscountRate: this.inputDiscountRate != null ? String(this.inputDiscountRate) : '',
            dcfMarginOfSafety: this.inputMarginOfSafety != null ? String(this.inputMarginOfSafety) : '',
            dcfCash: this.inputDcfCash != null ? formatWithSpaces(this.inputDcfCash / 1e6) : '',
            dcfDebt: this.inputDcfDebt != null ? formatWithSpaces(this.inputDcfDebt / 1e6) : '',
            dcfSharesOutstanding:
                this.inputDcfSharesOutstanding != null ? formatWithSpaces(this.inputDcfSharesOutstanding / 1e6) : '',
        };
    }

    get defaultFormValues(): FairPriceFormValues {
        const defaultTargetYield = this.fcfYield != null ? parseFloat(this.fcfYield.toFixed(2)) : null;

        return {
            peRequiredReturn: '',
            fcfRequiredReturn: '',
            growthRate: null,
            eps: this.eps?.toFixed(2) ?? '',
            peMultiple: this.peRatio?.toFixed(2) ?? '',
            fcfPerShare: this.fcfPerShare?.toFixed(2) ?? '',
            fcfGrowthRate: '',
            fcfTargetYield: defaultTargetYield?.toFixed(2) ?? '',

            dcfTotalFcf: this.apiDcfTotalFcf != null ? formatWithSpaces(this.apiDcfTotalFcf / 1e6) : '',
            dcfGrowthRate: '',
            dcfTerminalGrowthRate: '',
            dcfDiscountRate: '',
            dcfMarginOfSafety: '',
            dcfCash: this.apiDcfCash != null ? formatWithSpaces(this.apiDcfCash / 1e6) : '',
            dcfDebt: this.apiDcfDebt != null ? formatWithSpaces(this.apiDcfDebt / 1e6) : '',
            dcfSharesOutstanding:
                this.apiDcfSharesOutstanding != null ? formatWithSpaces(this.apiDcfSharesOutstanding / 1e6) : '',
        };
    }

    get canResetActiveTab(): boolean {
        const sameNumber = (a: number | null, b: number | null) => a === b;

        if (this.activeTab === FairPriceMethodEnum.PE) {
            return (
                !sameNumber(this.inputEps, this.eps) ||
                !sameNumber(this.inputPeMultiple, this.peRatio) ||
                this.inputGrowthRate !== null ||
                this.inputPeRequiredReturn !== null
            );
        }

        if (this.activeTab === FairPriceMethodEnum.FCF) {
            const defaultTargetYield = this.fcfYield != null ? parseFloat(this.fcfYield.toFixed(2)) : null;

            return (
                !sameNumber(this.inputFcfPerShare, this.fcfPerShare) ||
                this.inputGrowthRateFCF !== null ||
                !sameNumber(this.inputTargetFCFYield, defaultTargetYield) ||
                this.inputFcfRequiredReturn !== null
            );
        }

        if (this.activeTab === FairPriceMethodEnum.DCF) {
            return (
                !sameNumber(this.inputDcfTotalFcf, this.apiDcfTotalFcf) ||
                this.inputDcfGrowthRate !== null ||
                this.inputTerminalGrowthRate !== null ||
                this.inputDiscountRate !== null ||
                this.inputMarginOfSafety !== null ||
                !sameNumber(this.inputDcfCash, this.apiDcfCash) ||
                !sameNumber(this.inputDcfDebt, this.apiDcfDebt) ||
                !sameNumber(this.inputDcfSharesOutstanding, this.apiDcfSharesOutstanding) ||
                this.dcfYears !== DCF_YEARS
            );
        }

        return false;
    }

    get projectionChartData(): AreaChartMobxDto[] {
        if (!this.currentPrice) return [];

        const projectionYears = this.activeTab === FairPriceMethodEnum.DCF ? this.dcfYears : DCF_YEARS;
        const quarters = projectionYears * 4;
        const now = new Date();
        const growthRate = this.inputGrowthRate;
        const growthRateFCF = this.inputGrowthRateFCF ?? growthRate;

        const projectedPoints: AreaChartDataMobxDto[] = [];
        const currentPricePoints: AreaChartDataMobxDto[] = [];

        for (let i = 0; i <= quarters; i += 1) {
            const date = new Date(now.getFullYear(), now.getMonth() + i * 3, 1);
            const years = i / 4;

            let projectedValue = 0;

            switch (this.activeTab) {
                case FairPriceMethodEnum.PE:
                    if (
                        this.inputPeMultiple &&
                        this.inputEps &&
                        this.inputPeRequiredReturn != null &&
                        growthRate != null
                    ) {
                        projectedValue = calculateProjectionPE(
                            this.inputEps,
                            growthRate,
                            this.inputPeMultiple,
                            this.inputPeRequiredReturn,
                            years,
                        );
                    }
                    break;
                case FairPriceMethodEnum.FCF:
                    if (
                        this.inputFcfPerShare &&
                        this.inputFcfRequiredReturn &&
                        this.inputTargetFCFYield &&
                        growthRateFCF != null
                    ) {
                        projectedValue = calculateProjectionFCF(
                            this.inputFcfPerShare,
                            null,
                            growthRateFCF,
                            this.inputTargetFCFYield,
                            this.inputFcfRequiredReturn,
                            years,
                        );
                    }
                    break;
                case FairPriceMethodEnum.DCF:
                    if (
                        this.inputDcfTotalFcf &&
                        this.inputDcfGrowthRate &&
                        this.inputDcfSharesOutstanding &&
                        this.inputTerminalGrowthRate != null &&
                        this.inputDiscountRate != null
                    ) {
                        projectedValue = calculateProjectionDCF(
                            this.inputDcfTotalFcf,
                            this.inputDcfGrowthRate,
                            this.inputTerminalGrowthRate,
                            this.inputDiscountRate,
                            this.dcfYears,
                            years,
                            this.inputDcfCash ?? 0,
                            this.inputDcfDebt ?? 0,
                            this.inputDcfSharesOutstanding,
                        );
                    }
                    break;
                default:
                    break;
            }

            projectedPoints.push(Object.assign(new AreaChartDataMobxDto(), { x: date, y: +projectedValue.toFixed(2) }));
            currentPricePoints.push(Object.assign(new AreaChartDataMobxDto(), { x: date, y: this.currentPrice }));
        }

        return [
            Object.assign(new AreaChartMobxDto(), {
                id: 'intrinsicValue',
                chartDataType: null,
                color: '',
                data: projectedPoints,
            }),
            Object.assign(new AreaChartMobxDto(), {
                id: 'currentPrice',
                chartDataType: null,
                color: '',
                data: currentPricePoints,
            }),
        ];
    }

    setActiveTab(tab: FairPriceTab): void {
        this.activeTab = tab;
    }

    setInputEps(value: number | null): void {
        this.inputEps = value;
    }

    setInputPeMultiple(value: number | null): void {
        this.inputPeMultiple = value;
    }

    setInputGrowthRate(value: number | null): void {
        this.inputGrowthRate = value;
    }

    setInputGrowthRateFCF(value: number | null): void {
        this.inputGrowthRateFCF = value;
    }

    setInputPeRequiredReturn(value: number | null): void {
        this.inputPeRequiredReturn = value;
    }

    setInputFcfRequiredReturn(value: number | null): void {
        this.inputFcfRequiredReturn = value;
    }

    setInputFcfPerShare(value: number | null): void {
        this.inputFcfPerShare = value;
    }

    setInputTargetFCFYield(value: number | null): void {
        this.inputTargetFCFYield = value;
    }

    setInputDcfTotalFcf(value: number | null): void {
        this.inputDcfTotalFcf = value;
    }

    setInputDcfGrowthRate(value: number | null): void {
        this.inputDcfGrowthRate = value;
    }

    setInputTerminalGrowthRate(value: number | null): void {
        this.inputTerminalGrowthRate = value;
    }

    setInputDiscountRate(value: number | null): void {
        this.inputDiscountRate = value;
    }

    setInputMarginOfSafety(value: number | null): void {
        this.inputMarginOfSafety = value;
    }

    setInputDcfCash(value: number | null): void {
        this.inputDcfCash = value;
    }

    setInputDcfDebt(value: number | null): void {
        this.inputDcfDebt = value;
    }

    setInputDcfSharesOutstanding(value: number | null): void {
        this.inputDcfSharesOutstanding = value;
    }

    applyFormValues(values: FairPriceFormValues): void {
        const parseOrNull = (v: string | number | null): number | null => {
            const trimmed = stripSpaces(String(v ?? '')).replace(',', '.');
            if (trimmed === '') return null;
            const n = Number(trimmed);
            return Number.isFinite(n) ? n : null;
        };
        const scaleOrNull = (v: string | number | null): number | null => {
            const n = parseOrNull(v);
            return n != null ? n * 1e6 : null;
        };

        this.inputPeRequiredReturn = parseOrNull(values.peRequiredReturn);
        this.inputFcfRequiredReturn = parseOrNull(values.fcfRequiredReturn);
        this.inputEps = parseOrNull(values.eps);
        this.inputGrowthRate = parseOrNull(values.growthRate);
        this.inputPeMultiple = parseOrNull(values.peMultiple);
        this.inputFcfPerShare = parseOrNull(values.fcfPerShare);
        this.inputGrowthRateFCF = parseOrNull(values.fcfGrowthRate);
        this.inputTargetFCFYield = parseOrNull(values.fcfTargetYield);

        this.inputDcfTotalFcf = scaleOrNull(values.dcfTotalFcf);
        this.inputDcfGrowthRate = parseOrNull(values.dcfGrowthRate);
        this.inputTerminalGrowthRate = parseOrNull(values.dcfTerminalGrowthRate);
        this.inputDiscountRate = parseOrNull(values.dcfDiscountRate);
        this.inputMarginOfSafety = parseOrNull(values.dcfMarginOfSafety);
        this.inputDcfCash = scaleOrNull(values.dcfCash);
        this.inputDcfDebt = scaleOrNull(values.dcfDebt);
        this.inputDcfSharesOutstanding = scaleOrNull(values.dcfSharesOutstanding);
    }

    setDcfYears(value: 5 | 10): void {
        this.dcfYears = value;
    }

    async resetAndReload(): Promise<void> {
        if (!this.symbol) return;

        runInAction(() => {
            if (this.activeTab === FairPriceMethodEnum.PE) {
                this.inputEps = null;
                this.inputPeMultiple = null;
                this.inputGrowthRate = null;
                this.inputPeRequiredReturn = null;
            } else if (this.activeTab === FairPriceMethodEnum.FCF) {
                this.inputFcfPerShare = null;
                this.inputGrowthRateFCF = null;
                this.inputTargetFCFYield = null;
                this.inputFcfRequiredReturn = null;
            } else if (this.activeTab === FairPriceMethodEnum.DCF) {
                this.inputDcfTotalFcf = null;
                this.inputDcfGrowthRate = null;
                this.inputTerminalGrowthRate = null;
                this.inputDiscountRate = null;
                this.inputMarginOfSafety = null;
                this.inputDcfCash = null;
                this.inputDcfDebt = null;
                this.inputDcfSharesOutstanding = null;
                this.dcfYears = DCF_YEARS;
            }
        });

        await this.fetchGrowthRates(this.symbol, true);
    }

    async resetActiveTabAndSave(): Promise<boolean> {
        if (!this.symbol) return false;

        runInAction(() => {
            if (this.activeTab === FairPriceMethodEnum.PE) {
                this.inputEps = null;
                this.inputPeMultiple = null;
                this.inputGrowthRate = null;
                this.inputPeRequiredReturn = null;
            } else if (this.activeTab === FairPriceMethodEnum.FCF) {
                this.inputFcfPerShare = null;
                this.inputGrowthRateFCF = null;
                this.inputTargetFCFYield = null;
                this.inputFcfRequiredReturn = null;
            } else if (this.activeTab === FairPriceMethodEnum.DCF) {
                this.inputDcfTotalFcf = null;
                this.inputDcfGrowthRate = null;
                this.inputTerminalGrowthRate = null;
                this.inputDiscountRate = null;
                this.inputMarginOfSafety = null;
                this.inputDcfCash = null;
                this.inputDcfDebt = null;
                this.inputDcfSharesOutstanding = null;
                this.dcfYears = DCF_YEARS;
            }
        });

        const success = await this.saveFairPriceSettings();
        if (!success) return false;

        await this.fetchGrowthRates(this.symbol, true);
        return true;
    }

    async fetchGrowthRates(symbolId: string, skipSavedSettings = false): Promise<void> {
        try {
            this.loading = true;

            if (this.symbol !== symbolId) {
                this.inputEps = null;
                this.inputPeMultiple = null;
                this.inputGrowthRate = null;
                this.inputPeRequiredReturn = null;
                this.inputFcfRequiredReturn = null;
                this.inputGrowthRateFCF = null;
                this.inputFcfPerShare = null;
                this.inputTargetFCFYield = null;
                this.inputDcfTotalFcf = null;
                this.apiDcfTotalFcf = null;
                this.apiDcfCash = null;
                this.apiDcfDebt = null;
                this.apiDcfSharesOutstanding = null;
                this.inputDcfGrowthRate = null;
                this.inputTerminalGrowthRate = null;
                this.inputDiscountRate = null;
                this.inputMarginOfSafety = null;
                this.inputDcfCash = null;
                this.inputDcfDebt = null;
                this.inputDcfSharesOutstanding = null;
            }

            const fetchedRatios = await this.rootStore.apiClient.symbolController.symbolControllerGetRatios({
                symbolId,
            });

            const highlights = this.rootStore.symbolsStore.symbolDetailStore?.highlights;

            runInAction(() => {
                this.eps = fetchedRatios?.dilutedEpsTTM ?? highlights?.eps ?? null;
                this.peRatio = fetchedRatios?.peRatio ?? highlights?.peRatio ?? null;

                if (this.inputEps === null) {
                    this.inputEps = this.eps;
                }
                if (this.inputPeMultiple === null) {
                    this.inputPeMultiple = this.peRatio;
                }

                this.symbol = symbolId;
            });

            if (fetchedRatios) {
                runInAction(() => {
                    this.fcfPerShare = fetchedRatios.fcfPerShare ?? null;
                    this.fcfYield = fetchedRatios.fcfYield != null ? fetchedRatios.fcfYield * 100 : null;
                    if (this.inputFcfPerShare === null) {
                        this.inputFcfPerShare = fetchedRatios.fcfPerShare ?? null;
                    }
                    if (this.inputTargetFCFYield === null && this.fcfYield) {
                        this.inputTargetFCFYield = parseFloat(this.fcfYield.toFixed(2));
                    }

                    this.apiDcfTotalFcf = fetchedRatios.freeCashFlow ?? null;
                    this.apiDcfCash = fetchedRatios.cashAndShortTermInvestments ?? null;
                    this.apiDcfDebt = fetchedRatios.totalDebt ?? null;
                    this.apiDcfSharesOutstanding = fetchedRatios.sharesOutstanding ?? null;
                    if (this.inputDcfTotalFcf === null) {
                        this.inputDcfTotalFcf = fetchedRatios.freeCashFlow ?? null;
                    }
                    if (this.inputDcfCash === null) {
                        this.inputDcfCash = fetchedRatios.cashAndShortTermInvestments ?? null;
                    }
                    if (this.inputDcfDebt === null) {
                        this.inputDcfDebt = fetchedRatios.totalDebt ?? null;
                    }
                    if (this.inputDcfSharesOutstanding === null) {
                        this.inputDcfSharesOutstanding = fetchedRatios.sharesOutstanding ?? null;
                    }
                });
            }
            if (!skipSavedSettings) {
                await this.fetchFairPriceSettings(symbolId);
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.loading = false;
        }
    }

    async fetchFairPriceSettings(symbolId: string): Promise<void> {
        try {
            const settings =
                await this.rootStore.apiClient.symbolFairPriceController.symbolFairPriceControllerGetBySymbolId({
                    symbolId,
                });
            if (!settings) return;

            runInAction(() => {
                this.activeTab = settings.selectedMethod as unknown as FairPriceTab;

                if (settings.peRequiredReturn !== null) this.inputPeRequiredReturn = settings.peRequiredReturn;
                if (settings.peGrowthRate !== null) this.inputGrowthRate = settings.peGrowthRate;
                if (settings.peMultiple !== null) this.inputPeMultiple = settings.peMultiple;

                if (settings.fcfRequiredReturn !== null) this.inputFcfRequiredReturn = settings.fcfRequiredReturn;
                if (settings.fcfGrowthRate !== null) this.inputGrowthRateFCF = settings.fcfGrowthRate;
                if (settings.fcfTargetYield !== null) this.inputTargetFCFYield = settings.fcfTargetYield;

                if (settings.dcfGrowthRate !== null) this.inputDcfGrowthRate = settings.dcfGrowthRate;
                if (settings.dcfTerminalGrowthRate !== null)
                    this.inputTerminalGrowthRate = settings.dcfTerminalGrowthRate;
                if (settings.dcfDiscountRate !== null) this.inputDiscountRate = settings.dcfDiscountRate;
                if (settings.dcfMarginOfSafety !== null) this.inputMarginOfSafety = settings.dcfMarginOfSafety;
                if (settings.dcfYears !== null) this.dcfYears = settings.dcfYears as 5 | 10;
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
        }
    }

    async saveFairPriceSettings(): Promise<boolean> {
        if (!this.symbol) return false;
        try {
            await this.rootStore.apiClient.symbolFairPriceController.symbolFairPriceControllerUpsert({
                upsertSymbolFairPriceDto: {
                    symbolId: this.symbol,
                    selectedMethod: this.activeTab as unknown as UpsertSymbolFairPriceDtoSelectedMethodEnum,
                    peRequiredReturn: this.inputPeRequiredReturn,
                    peGrowthRate: this.inputGrowthRate,
                    peMultiple: this.inputPeMultiple,
                    fcfRequiredReturn: this.inputFcfRequiredReturn,
                    fcfGrowthRate: this.inputGrowthRateFCF,
                    fcfTargetYield: this.inputTargetFCFYield,
                    dcfGrowthRate: this.inputDcfGrowthRate,
                    dcfTerminalGrowthRate: this.inputTerminalGrowthRate,
                    dcfDiscountRate: this.inputDiscountRate,
                    dcfMarginOfSafety: this.inputMarginOfSafety,
                    dcfYears: this.dcfYears,
                    dcfCash: this.inputDcfCash,
                    dcfDebt: this.inputDcfDebt,
                    dcfSharesOutstanding: this.inputDcfSharesOutstanding,
                },
            });
            return true;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
            return false;
        }
    }
}
